// =============================================================================
// src/features/copilot-chat/index.ts
// Copiloto IA (chat) — markup do takeover + COMPORTAMENTO em TS (aposenta
//   assets/copilot-chat.js).
//
// Proveniencia: porte FIEL de unified.html 2759-2818 (markup) e de
//   assets/copilot-chat.js 12-890 (runtime). buildCopilotChatTakeoverMarkup()
//   devolve o HTML; initCopilotChat(ctx) liga o comportamento; openCopilotChat(text)
//   e a API publica (mesmo contrato do legado).
//
// CORRECAO DO BUG "Nova pergunta"/Ctrl+J: o init publica a impl REAL tanto em
//   `window.openCopilotChat` (onclick inline) QUANTO em `ctx.commands.openCopilotChat`
//   (consumido pela sidebar TS — scroll-spy-observer.ts). No legado, a sidebar
//   chamava o fallback-toast de commands.ts; agora ambos apontam para o chat real.
// =============================================================================

import type { AppContext } from '../../app/types';
import { resolveSentinelLiveConfig } from '../../data/sentinel-live-config';
import { collectCopilotChatRefs } from './copilot-dom';
import { CopilotConversation, type CopilotLlmConfig } from './copilot-conversation';
import { CopilotChatTakeover } from './copilot-takeover';
import { resolveOwnerIdentity } from '../../app/owner-identity';

export { COPILOT_CHAT_DATA } from './copilot-dataset';

/** Markup do takeover. Thunk (não const) para ler resolveOwnerIdentity() em call-time
 *  (após a hidratação da identidade no bootstrap), não no load do módulo. */
const renderMarkup = (): string => `<div id="copilotChatTakeover" class="cop-takeover" role="dialog" aria-modal="true" aria-labelledby="copTitle" hidden>
  <header class="cop-head">
    <div class="cop-brand">
      <span class="ai-orb ai-orb--sm" aria-hidden="true"><svg class="ai-orb__svg" viewBox="0 0 32 32"><use href="#aiOrbSym"/></svg></span>
      <div>
        <div class="cop-brand-title" id="copTitle">Copiloto IA</div>
        <div class="cop-brand-sub">Mapa Político · Manaus</div>
      </div>
    </div>
    <div class="cop-ctx">
      <span class="cop-ctx-avatar" aria-hidden="true">${resolveOwnerIdentity().initials}</span>
      <div style="min-width:0">
        <div class="cop-ctx-name">${resolveOwnerIdentity().name}</div>
        <div class="cop-ctx-meta"><span class="cop-status"><span class="cop-status-pip" aria-hidden="true"></span>online</span> · ${resolveOwnerIdentity().meta}</div>
      </div>
    </div>
    <div class="cop-head-spacer"></div>
    <button type="button" class="cop-head-btn" id="copNewThread" title="Nova conversa" aria-label="Nova conversa"><i class="ti ti-message-plus cop-ico sm" aria-hidden="true"></i></button>
    <button type="button" class="cop-close" id="copClose" aria-label="Voltar ao mapa"><i class="ti ti-x cop-ico sm" aria-hidden="true"></i> <span>Voltar ao mapa</span></button>
  </header>

  <div class="cop-scroll" id="copScroll">
    <div class="cop-thread" id="copThread" role="log" aria-label="Conversa com o Copiloto IA">
      <section class="cop-hero" id="copHero">
        <span class="ai-orb ai-orb--lg" role="img" aria-label="Copiloto IA"><svg class="ai-orb__svg" viewBox="0 0 32 32"><use href="#aiOrbSym"/></svg></span>
        <h2 class="cop-hero-title">Como está a imagem do mandato hoje?</h2>
        <p class="cop-hero-sub">Pergunte sobre menções, reputação, crises ou o Senado. Eu consulto as bases, mostro o raciocínio e proponho um plano para você aprovar.</p>
        <div class="cop-hero-chips" role="group" aria-label="Perguntas sugeridas">
          <button type="button" class="cop-suggest" data-prompt="Como está a reputação do mandato hoje?"><i class="ti ti-gauge cop-ico sm" aria-hidden="true"></i> Reputação hoje</button>
          <button type="button" class="cop-suggest" data-prompt="Qual o nível de ameaça do tema do pronto-socorro da Zona Leste e de onde está vindo? Quero ver as ocorrências."><i class="ti ti-alert-triangle cop-ico sm" aria-hidden="true"></i> Pronto-socorro Zona Leste</button>
          <button type="button" class="cop-suggest" data-prompt="Resuma o sentimento das menções nas últimas 24h"><i class="ti ti-mood-neutral cop-ico sm" aria-hidden="true"></i> Sentimento 24h</button>
          <button type="button" class="cop-suggest" data-prompt="Monte um diagnóstico e um plano de resposta até as 14h"><i class="ti ti-clipboard-check cop-ico sm" aria-hidden="true"></i> Plano de resposta</button>
          <button type="button" class="cop-suggest" data-prompt="Mostre a evolução do índice de reputação nos últimos 90 dias"><i class="ti ti-chart-line cop-ico sm" aria-hidden="true"></i> Evolução 90 dias</button>
          <button type="button" class="cop-suggest" data-prompt="Compare nosso Share of Voice com os outros parlamentares no Senado"><i class="ti ti-chart-bar cop-ico sm" aria-hidden="true"></i> Share of Voice</button>
          <button type="button" class="cop-suggest" data-prompt="Monte a linha do tempo da narrativa do tema da obra"><i class="ti ti-timeline cop-ico sm" aria-hidden="true"></i> Linha do tempo</button>
          <button type="button" class="cop-suggest" data-prompt="Qual o volume de menções nas últimas 24h por canal?"><i class="ti ti-broadcast cop-ico sm" aria-hidden="true"></i> Volume por canal</button>
        </div>
      </section>
    </div>
  </div>

  <footer class="cop-composer">
    <div class="cop-composer-inner">
      <div class="cop-composer-chips" role="group" aria-label="Prompts sugeridos">
        <button type="button" class="cop-chip-prompt" data-prompt="Monte um diagnóstico e um plano de resposta até as 14h"><i class="ti ti-stethoscope cop-ico sm" aria-hidden="true"></i> Diagnóstico + plano</button>
        <button type="button" class="cop-chip-prompt" data-prompt="Gerar nota de esclarecimento com base no edital"><i class="ti ti-file-text cop-ico sm" aria-hidden="true"></i> Gerar nota</button>
        <button type="button" class="cop-chip-prompt" data-prompt="Qual o nível de ameaça do tema do pronto-socorro?"><i class="ti ti-alert-triangle cop-ico sm" aria-hidden="true"></i> Nível de ameaça</button>
      </div>
      <form class="cop-composer-bar" id="copForm" autocomplete="off">
        <button type="button" class="cop-attach" id="copAttach" aria-label="Anexar arquivo" title="Anexar"><i class="ti ti-paperclip cop-ico" aria-hidden="true"></i></button>
        <label class="cop-input-wrap"><span class="cop-sr-only">Pergunte ao Copiloto IA</span><textarea id="copInput" class="cop-input" rows="1" placeholder="Pergunte ao Copiloto IA…" aria-label="Pergunte ao Copiloto IA"></textarea></label>
        <button type="submit" class="cop-send" id="copSend" aria-label="Enviar" disabled><i class="ti ti-arrow-up cop-ico" aria-hidden="true"></i></button>
      </form>
      <p class="cop-composer-hint">O Copiloto pode errar; revise antes de publicar.</p>
    </div>
  </footer>

  <div id="copLive" class="cop-sr-only" aria-live="polite"></div>
</div>`;

/** Devolve a string HTML literal deste bloco. */
export function buildCopilotChatTakeoverMarkup(): string {
  return renderMarkup();
}

/**
 * Impl REAL viva apos initCopilotChat. Antes do init e null; o `openCopilotChat`
 *   exportado delega para ela (late-binding), espelhando o legado que so existia
 *   apos o IIFE rodar (assets/copilot-chat.js so define window.openCopilotChat la dentro).
 */
let liveOpenCopilotChat: ((text: string) => void) | null = null;

/**
 * Liga o comportamento do chat (porte do IIFE de assets/copilot-chat.js).
 *   Aborta em silencio se #copilotChatTakeover nao existir (porte do early return, 17).
 *   Publica a impl real no window e no `ctx.commands` (correcao do bug Ctrl+J).
 *
 * @example
 *   initCopilotChat(appContext); // chamado por main.ts apos montar o #routeLayer
 */
export function initCopilotChat(ctx: AppContext): void {
  const refs = collectCopilotChatRefs();
  if (!refs) return;
  const llmConfig = buildCopilotLlmConfig(ctx);
  const conversation = new CopilotConversation(refs, llmConfig);
  const takeover = new CopilotChatTakeover(refs, conversation);
  takeover.init();
  publishOpenCopilotChat(ctx, takeover.openCopilotChat);
}

function buildCopilotLlmConfig(ctx: AppContext): CopilotLlmConfig | null {
  const live = resolveSentinelLiveConfig(import.meta.env);
  if (!live.enabled || !live.copilotLlmEnabled) return null;
  return {
    enabled: true,
    apiUrl: live.apiEndpoint,
    clientId: live.clientId,
  };
}

/**
 * Publica a impl real do chat onde quer que `openCopilotChat` seja consumido:
 *   (1) `window.openCopilotChat` para os onclick inline herdados; (2)
 *   `ctx.commands.openCopilotChat` para a sidebar TS (scroll-spy-observer.ts),
 *   substituindo o fallback-toast de commands.ts. Sem isso, "Nova pergunta"/Ctrl+J
 *   continuariam abrindo apenas o toast (o bug original).
 */
function publishOpenCopilotChat(ctx: AppContext, impl: (text: string) => void): void {
  liveOpenCopilotChat = impl;
  window.openCopilotChat = impl;
  const commands = ctx.commands as { openCopilotChat: (text: string) => void };
  commands.openCopilotChat = impl;
}

/**
 * API publica do chat (mesmo contrato de window.openCopilotChat do legado).
 *   Delega para a impl viva; antes do init, faz no-op seguro (o takeover ainda
 *   nao montou) em vez de lancar — preserva a tolerancia do mockup.
 */
export function openCopilotChat(text: string): void {
  if (liveOpenCopilotChat) liveOpenCopilotChat(text);
}
